import { parse, serialize } from "cookie";

export const COOKIE_NAMES = {
  ACCESS_TOKEN: "auth_access_token",
  REFRESH_TOKEN: "auth_refresh_token",
  EXPIRES_AT: "auth_expires_at",
  ORG: "auth_org",
  ORG_NAME: "auth_org_name",
  JOBBER_ACCOUNT_ID: "auth_jobber_account_id",
} as const;

export interface CookieOptions {
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: "strict" | "lax" | "none";
  maxAge?: number;
  path?: string;
  domain?: string;
}

export class CookieService {
  static isClient(): boolean {
    return typeof window !== "undefined";
  }

  static isProduction(): boolean {
    return process.env.NODE_ENV === "production";
  }

  static getDefaultOptions(httpOnly: boolean = true): CookieOptions {
    return {
      httpOnly,
      secure: CookieService.isProduction(),
      sameSite: "lax",
      path: "/",
    };
  }

  static serialize(
    name: string,
    value: string,
    options: CookieOptions = {},
  ): string {
    const finalOptions = {
      ...CookieService.getDefaultOptions(options.httpOnly),
      ...options,
    };

    return serialize(name, value, finalOptions);
  }

  static parse(cookieString: string): Record<string, string> {
    return parse(cookieString || "") as Record<string, string>;
  }

  static getFromBrowser(name: string): string | undefined {
    if (!CookieService.isClient()) {
      return undefined;
    }

    const cookies = CookieService.parse(document.cookie);
    return cookies[name];
  }

  static setInBrowser(
    name: string,
    value: string,
    options: CookieOptions = {},
  ): void {
    if (!CookieService.isClient()) {
      return;
    }

    const cookieOptions = {
      ...options,
      httpOnly: false,
    };

    document.cookie = CookieService.serialize(name, value, cookieOptions);
  }

  static removeFromBrowser(name: string): void {
    if (!CookieService.isClient()) {
      return;
    }

    document.cookie = CookieService.serialize(name, "", {
      httpOnly: false,
      maxAge: 0,
      path: "/",
    });
  }

  static getAuthCookieOptions(
    type: "access" | "refresh" | "metadata",
  ): CookieOptions {
    const baseOptions = CookieService.getDefaultOptions(type !== "metadata");

    switch (type) {
      case "access":
        return {
          ...baseOptions,
          maxAge: 2592000,
        };
      case "refresh":
        return {
          ...baseOptions,
          maxAge: 2592000,
        };
      case "metadata":
        return {
          ...baseOptions,
          httpOnly: false,
          maxAge: 2592000,
        };
      default:
        return baseOptions;
    }
  }

  static createAuthCookies(
    accessToken: string,
    refreshToken: string,
    expiresAt: number,
    org: string,
    orgName?: string,
    jobberAccountId?: string,
  ): string[] {
    const cookies = [
      CookieService.serialize(
        COOKIE_NAMES.ACCESS_TOKEN,
        accessToken,
        CookieService.getAuthCookieOptions("access"),
      ),
      CookieService.serialize(
        COOKIE_NAMES.REFRESH_TOKEN,
        refreshToken,
        CookieService.getAuthCookieOptions("refresh"),
      ),
      CookieService.serialize(
        COOKIE_NAMES.EXPIRES_AT,
        expiresAt.toString(),
        CookieService.getAuthCookieOptions("metadata"),
      ),
      CookieService.serialize(
        COOKIE_NAMES.ORG,
        org,
        CookieService.getAuthCookieOptions("metadata"),
      ),
    ];

    if (orgName) {
      cookies.push(
        CookieService.serialize(
          COOKIE_NAMES.ORG_NAME,
          orgName,
          CookieService.getAuthCookieOptions("metadata"),
        ),
      );
    }

    if (jobberAccountId) {
      cookies.push(
        CookieService.serialize(
          COOKIE_NAMES.JOBBER_ACCOUNT_ID,
          jobberAccountId,
          CookieService.getAuthCookieOptions("metadata"),
        ),
      );
    }

    return cookies;
  }

  static clearAuthCookies(): string[] {
    const clearOptions: CookieOptions = {
      maxAge: 0,
      path: "/",
      secure: CookieService.isProduction(),
      sameSite: "lax",
    };

    return [
      CookieService.serialize(COOKIE_NAMES.ACCESS_TOKEN, "", {
        ...clearOptions,
        httpOnly: true,
      }),
      CookieService.serialize(COOKIE_NAMES.REFRESH_TOKEN, "", {
        ...clearOptions,
        httpOnly: true,
      }),
      CookieService.serialize(COOKIE_NAMES.EXPIRES_AT, "", clearOptions),
      CookieService.serialize(COOKIE_NAMES.ORG, "", clearOptions),
      CookieService.serialize(COOKIE_NAMES.ORG_NAME, "", clearOptions),
      CookieService.serialize(COOKIE_NAMES.JOBBER_ACCOUNT_ID, "", clearOptions),
    ];
  }
}
